import React, { startTransition, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRightIcon } from "@phosphor-icons/react";
import Newspaper from "../Manifesto/Newspaper";
import useReducedMotion from "../../lib/useReducedMotion";
import { isEnglishPath, localizedPath } from "../../utils/seoLinks";
import unfoldNewspaper from "./unfoldNewspaper";
import { useHomeLang } from "./homeLang";
import "./manifesto.css";

const loadPage = () => import("../Manifesto/ManifestoPage");
// The paper's print is the manifesto's page itself (Newspaper.jsx), in the
// language that page is written in; only the words round it change.
const WORDS = {
  it: { heading: "Più tecnologia, più umanità.", read: "Leggi il manifesto" },
  en: { heading: "More technology, more humanity.", read: "Read the manifesto" },
};
// The server's guess at the window, and at the broadsheet's width.
const SERVER_WINDOW = { height: 900, header: 68, broadsheet: 1280 };

// The folded copy is always the broadsheet, whatever the window, so a phone
// shows the same front page as a desktop, only smaller. It is tall enough
// to cover the window under the header once open; where the window is as
// wide as the broadsheet, the manifesto's page is exactly this sheet.
function pageSheet({ height, header, broadsheet }) {
  return { width: broadsheet, height: Math.round(Math.max(broadsheet * 1.2, height - header)) };
}

function measureWindow(paper) {
  return {
    height: window.innerHeight,
    header: document.querySelector(".hf .sn")?.offsetHeight ?? 0,
    broadsheet: parseFloat(getComputedStyle(paper.querySelector(".np")).maxWidth),
  };
}

// One accessible link is the entire folded newspaper. Its decorative print
// is also the standalone page, so the last animation frame matches that page.
export default function ManifestoSection() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const reducedMotion = useReducedMotion();
  const words = WORDS[useHomeLang()];
  const paper = useRef(null);
  const backdrop = useRef(null);
  const opening = useRef(false);
  const alive = useRef(true);
  const [sheet, setSheet] = useState(() => pageSheet(SERVER_WINDOW));
  const [paperWidth, setPaperWidth] = useState(880);
  const scale = paperWidth / sheet.width;
  const to = localizedPath("/manifesto", isEnglishPath(pathname) ? "en" : "it");
  const hold = useRef(event => event.preventDefault());
  const preload = () => {
    loadPage();
    if (!reducedMotion) import("./newspaperTurn").catch(error => console.error("Could not prepare the newspaper:", error));
  };

  useLayoutEffect(() => {
    const read = () => {
      if (opening.current) return;
      setPaperWidth(paper.current.clientWidth || Math.min(880, window.innerWidth - 48));
      setSheet(pageSheet(measureWindow(paper.current)));
    };
    read();
    // Recheck after the initial scale is applied. A mobile browser can
    // temporarily expand its viewport around the server's desktop sheet.
    const settledFrame = window.requestAnimationFrame(read);
    window.addEventListener("resize", read);
    const watcher = new ResizeObserver(read);
    watcher.observe(paper.current);
    return () => {
      window.removeEventListener("resize", read);
      window.cancelAnimationFrame(settledFrame);
      watcher.disconnect();
    };
  }, []);

  useEffect(() => {
    alive.current = true;
    const stop = hold.current;
    return () => {
      alive.current = false;
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchmove", stop);
    };
  }, []);

  const open = async event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (opening.current) return;
    opening.current = true;
    try {
      const page = loadPage();
      if (!reducedMotion) {
        window.addEventListener("wheel", hold.current, { passive: false });
        window.addEventListener("touchmove", hold.current, { passive: false });
        await Promise.all([page, unfoldNewspaper(paper.current, backdrop.current, scale, measureWindow(paper.current).header)]);
      } else {
        await page;
      }
      if (alive.current) startTransition(() => navigate(to));
    } catch (error) {
      if (!alive.current) return;
      console.error("Could not unfold the manifesto:", error);
      window.location.assign(to);
    }
  };

  return (
    <section id="manifesto" aria-labelledby="hf-h-man" className="hf-sec hf-wrap mf">
      <h2 id="hf-h-man" className="hf-sr">{words.heading}</h2>
      <Link
        ref={paper}
        to={to}
        className="mf-paper"
        aria-label={words.read}
        onClick={open}
        onPointerEnter={preload}
        onFocus={preload}
        style={{ "--w": `${sheet.width}px`, "--h": `${sheet.height}px`, "--s": scale, height: (sheet.height / 2) * scale }}
      >
        <div className="mf-object" aria-hidden="true">
          <span className="mf-leaf mf-leaf-outer" />
          <span className="mf-leaf mf-leaf-lower" />
          <span className="mf-leaf mf-leaf-middle" />
          <span className="mf-leaf mf-leaf-upper" />
          <span className="mf-leaf mf-leaf-inner" />
          <div className="mf-sheet" inert="">
            <div className="mf-top"><Newspaper replica /></div>
          </div>
          <span className="mf-edge" />
          {/* Under the light, as the print is. */}
          <span className="mf-read">{words.read} <ArrowRightIcon aria-hidden /></span>
          <span className="mf-shade" />
        </div>
      </Link>
      <div ref={backdrop} className="mf-backdrop" />
    </section>
  );
}
